import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { AUTH } from "@/constants/testIds";
import PasswordInput from "@/components/PasswordInput";

/**
 * V2 invitation-aware registration page.
 *
 * Three states driven by the `?token=` query string:
 *
 *   1. No token            -> classic open registration form (legacy path).
 *   2. token=XXX, new user -> "You're invited to <Club>" landing, full form,
 *                             on submit the invitation is auto-accepted and
 *                             the user lands in the dashboard.
 *   3. token=XXX, existing -> "We see you already have an account" sign-in
 *                             form (email is locked). On submit we sign in
 *                             and then POST /public/invitations/accept which
 *                             attaches the club to the existing account.
 */
export default function RegisterPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  if (token) {
    return <InvitationRegister token={token} />;
  }
  return <OpenRegister />;
}

function OpenRegister() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await register(form);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell eyebrow="Members' Registration" title="Join the Ledger">
      <p className="text-[#4A4A4A] mt-3">Your details remain private to the club and the pro shop.</p>
      {error && (
        <div
          data-testid={AUTH.errorBanner}
          className="mt-6 px-4 py-3 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] text-sm"
        >
          {error}
        </div>
      )}
      <form onSubmit={handleSubmit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="sm:col-span-2">
          <label className="label-elegant block mb-2">Full Name</label>
          <input
            type="text" required value={form.name} onChange={update("name")}
            data-testid={AUTH.registerName} className="input-field"
            placeholder="Edward C. Sutherland"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="label-elegant block mb-2">Email</label>
          <input
            type="email" required value={form.email} onChange={update("email")}
            data-testid={AUTH.registerEmail} className="input-field"
            placeholder="member@oakridgegolf.club"
          />
        </div>
        <div>
          <label className="label-elegant block mb-2">Phone</label>
          <input
            type="tel" value={form.phone} onChange={update("phone")}
            data-testid={AUTH.registerPhone} className="input-field"
            placeholder="(555) 123-4567"
          />
        </div>
        <div>
          <label className="label-elegant block mb-2">&nbsp;</label>
          <p className="text-xs text-[#7A7A7A] leading-snug mt-2">
            You&rsquo;ll add your club affiliations &amp; member numbers from your profile once you&rsquo;re signed in.
          </p>
        </div>
        <div className="sm:col-span-2">
          <label className="label-elegant block mb-2">Password</label>
          <PasswordInput
            required minLength={6} value={form.password} onChange={update("password")}
            data-testid={AUTH.registerPassword} className="input-field"
            placeholder="At least 6 characters"
          />
        </div>
        <div className="sm:col-span-2 flex items-center justify-between mt-2">
          <Link to="/login" data-testid={AUTH.registerLoginLink} className="text-sm text-[#1A422D] underline underline-offset-4">
            Already a member? Sign in
          </Link>
          <button type="submit" disabled={submitting} data-testid={AUTH.registerSubmit} className="btn-primary">
            {submitting ? "Submitting…" : "Create Account"}
          </button>
        </div>
      </form>
    </PageShell>
  );
}

function InvitationRegister({ token }) {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/public/invitations/validate?token=${encodeURIComponent(token)}`);
      setInvite(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { reload(); }, [reload]);

  if (loading) {
    return (
      <PageShell eyebrow="Invitation" title="Validating your invitation…">
        <p data-testid="invite-validating" className="text-[#4A4A4A] mt-3">One moment.</p>
      </PageShell>
    );
  }

  if (error || !invite) {
    return (
      <PageShell eyebrow="Invitation" title="Invitation unavailable">
        <div data-testid="invite-error" className="mt-6 px-4 py-3 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] text-sm">
          {error || "This invitation could not be found."}
        </div>
        <div className="mt-6">
          <Link to="/login" className="text-sm text-[#1A422D] underline underline-offset-4">Go to sign in →</Link>
        </div>
      </PageShell>
    );
  }

  if (invite.status !== "pending") {
    return (
      <PageShell eyebrow="Invitation" title="This invitation can no longer be used">
        <div data-testid="invite-status-banner" className="mt-6 px-4 py-3 border border-[#B59A5A] bg-[#F8F1DE] text-[#8a6f33] text-sm">
          {invite.message}
        </div>
        <p className="text-[#4A4A4A] mt-4 text-sm">
          Ask the pro shop at <strong>{invite.club?.name}</strong> to send you a fresh invitation.
        </p>
        <div className="mt-6 flex items-center gap-4">
          <Link to="/login" className="btn-secondary text-xs">Sign in</Link>
          <Link to="/" className="text-sm text-[#1A422D] underline underline-offset-4">Back to home</Link>
        </div>
      </PageShell>
    );
  }

  if (invite.already_in_club) {
    return (
      <PageShell eyebrow="Invitation" title={`You're already a member of ${invite.club?.name}`}>
        <p data-testid="invite-already-member" className="text-[#4A4A4A] mt-3">
          We found an active membership on your account. Just sign in to continue.
        </p>
        <Link to="/login" className="btn-primary inline-flex mt-6">Sign in</Link>
      </PageShell>
    );
  }

  if (invite.existing_user) {
    return (
      <ExistingUserAccept
        invite={invite}
        token={token}
        onSuccess={(user) => {
          setUser(user);
          navigate("/dashboard", { replace: true });
        }}
      />
    );
  }

  return (
    <NewUserAccept
      invite={invite}
      token={token}
      onSuccess={(user) => {
        setUser(user);
        navigate("/dashboard", { replace: true });
      }}
    />
  );
}

function NewUserAccept({ invite, token, onSuccess }) {
  const [form, setForm] = useState({
    name: invite.name_hint || "",
    password: "",
    mobile_number: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/public/invitations/accept", {
        token,
        name: form.name.trim(),
        password: form.password,
        mobile_number: form.mobile_number.trim() || undefined,
      });
      if (data?.user) onSuccess(data.user);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell eyebrow="Personal Invitation" title={`Join ${invite.club?.name}`}>
      <div data-testid="invite-club-banner" className="mt-4 p-4 border border-[#E5E3D8] bg-[#FAF9F2] text-sm text-[#4A4A4A]">
        <div className="overline mb-1">Invited Club</div>
        <div className="font-serif text-xl text-[#1A1A1A]">{invite.club?.name}</div>
        {invite.club?.city && <div className="text-xs text-[#7A7A7A] mt-1">{invite.club.city}</div>}
        {invite.club?.public_about && (
          <p className="mt-3 text-sm text-[#4A4A4A] leading-snug">{invite.club.public_about}</p>
        )}
      </div>
      <p className="text-[#4A4A4A] mt-4">
        Set up your My Crest Club account. You&rsquo;ll be welcomed into{" "}
        <strong>{invite.club?.name}</strong> as soon as you finish.
      </p>

      {error && (
        <div data-testid="invite-error" className="mt-6 px-4 py-3 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] text-sm">
          {error}
        </div>
      )}

      <form onSubmit={submit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="sm:col-span-2">
          <label className="label-elegant block mb-2">Email</label>
          <input
            data-testid="invite-email-locked"
            type="email" value={invite.email} disabled readOnly className="input-field opacity-75 cursor-not-allowed"
          />
          <p className="text-xs text-[#7A7A7A] mt-1">This is the address the pro shop invited.</p>
        </div>
        <div className="sm:col-span-2">
          <label className="label-elegant block mb-2">Full Name</label>
          <input
            type="text" required value={form.name} onChange={update("name")}
            data-testid="invite-name" className="input-field"
            placeholder="Edward C. Sutherland"
          />
        </div>
        <div>
          <label className="label-elegant block mb-2">Mobile (optional)</label>
          <input
            type="tel" value={form.mobile_number} onChange={update("mobile_number")}
            data-testid="invite-mobile" className="input-field"
            placeholder="(555) 123-4567"
          />
        </div>
        <div>
          <label className="label-elegant block mb-2">Password</label>
          <PasswordInput
            required minLength={8} value={form.password} onChange={update("password")}
            data-testid="invite-password" className="input-field"
            placeholder="At least 8 characters"
          />
        </div>
        <div className="sm:col-span-2 flex items-center justify-between mt-2">
          <Link to="/login" className="text-sm text-[#1A422D] underline underline-offset-4">
            Have an account? Sign in
          </Link>
          <button type="submit" disabled={busy} data-testid="invite-accept-submit" className="btn-primary">
            {busy ? "Joining…" : `Join ${invite.club?.name || "the club"}`}
          </button>
        </div>
      </form>
    </PageShell>
  );
}

function ExistingUserAccept({ invite, token, onSuccess }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      // Sign in first, then accept the invitation against the now-authenticated session.
      await api.post("/auth/login", { email: invite.email, password });
      const { data } = await api.post("/public/invitations/accept", { token });
      if (data?.user) onSuccess(data.user);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(formatApiErrorDetail(detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell eyebrow="Personal Invitation" title={`Add ${invite.club?.name} to your account`}>
      <div data-testid="invite-existing-banner" className="mt-4 p-4 border border-[#E5E3D8] bg-[#FAF9F2] text-sm text-[#4A4A4A]">
        We found an existing My Crest Club account for <strong>{invite.email}</strong>. Sign in to add{" "}
        <strong>{invite.club?.name}</strong> to your memberships.
      </div>
      {error && (
        <div data-testid="invite-error" className="mt-6 px-4 py-3 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] text-sm">
          {error}
        </div>
      )}
      <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-5">
        <div>
          <label className="label-elegant block mb-2">Email</label>
          <input
            data-testid="invite-email-locked"
            type="email" value={invite.email} disabled readOnly className="input-field opacity-75 cursor-not-allowed"
          />
        </div>
        <div>
          <label className="label-elegant block mb-2">Password</label>
          <PasswordInput
            required value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="invite-existing-password" className="input-field" placeholder="Your password"
          />
        </div>
        <div className="flex items-center justify-between">
          <Link to="/forgot-password" className="text-sm text-[#1A422D] underline underline-offset-4">Forgot password?</Link>
          <button type="submit" disabled={busy || !password} data-testid="invite-existing-submit" className="btn-primary">
            {busy ? "Joining…" : `Sign in & join ${invite.club?.name || ""}`}
          </button>
        </div>
      </form>
    </PageShell>
  );
}

function PageShell({ eyebrow, title, children }) {
  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-6 sm:p-12">
      <div data-testid="register-page" className="w-full max-w-xl surface-card p-8 sm:p-12 fade-up">
        <div className="overline">{eyebrow}</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-2 leading-none">{title}</h1>
        {children}
      </div>
    </div>
  );
}
